import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  useTheme,
  useMediaQuery,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Stack,
  Button,
} from '@mui/material';
import {
  ProductionQuantityLimits,
  Build,
  LocalShipping,
  SupportAgent,
  CheckCircle,
  Business,
  Park,
  PrecisionManufacturing,
  AutoAwesome,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { BRAND } from '../config/brand';
import { BrandHero } from '../components/brand/BrandHero';

interface Product {
  id: string;
  title: string;
  description: string;
  category: string;
  features: string[];
  specifications: string[];
  applications: string[];
  icon: React.ReactElement;
  image: string;
  price: string;
}

const products: Product[] = [
  {
    id: '1',
    title: 'Premium Cotton Textiles',
    description: 'High-quality cotton fabrics for fashion and industrial applications',
    category: 'Textiles',
    features: [
      '100% organic cotton',
      'Sustainable production',
      'Multiple color options',
      'Custom patterns available',
    ],
    specifications: [
      'Weight: 150-300 GSM',
      'Width: 44-60 inches',
      'Thread count: 200-400',
      'Finish: Soft, durable',
    ],
    applications: [
      'Fashion and apparel',
      'Home textiles',
      'Industrial applications',
      'Medical textiles',
    ],
    icon: <ProductionQuantityLimits sx={{ fontSize: 50, color: '#6366f1' }} />,
    image: BRAND.images.teamSign,
    price: 'KES 500-2,000 per meter',
  },
  {
    id: '2',
    title: 'Smart Office Furniture',
    description: 'Innovative office furniture with integrated technology',
    category: 'Furniture',
    features: [
      'IoT integration',
      'Ergonomic design',
      'Modular components',
      'Wireless charging',
    ],
    specifications: [
      'Material: Premium wood/steel',
      'Weight capacity: 150kg',
      'Power: USB-C, wireless charging',
      'Connectivity: Bluetooth, WiFi',
    ],
    applications: [
      'Modern offices',
      'Co-working spaces',
      'Conference rooms',
      'Home offices',
    ],
    icon: <Build sx={{ fontSize: 50, color: '#06b6d4' }} />,
    image: BRAND.images.furniture,
    price: 'KES 25,000-150,000',
  },
  {
    id: '3',
    title: 'Precision Metal Components',
    description: 'High-precision metal parts for automotive and industrial use',
    category: 'Metalworks',
    features: [
      'CNC precision machining',
      'Quality certified',
      'Custom specifications',
      'Fast turnaround',
    ],
    specifications: [
      'Tolerance: ±0.01mm',
      'Materials: Steel, aluminum, titanium',
      'Surface finish: Ra 0.8-3.2',
      'Certification: ISO 9001',
    ],
    applications: [
      'Automotive industry',
      'Aerospace components',
      'Medical devices',
      'Industrial machinery',
    ],
    icon: <PrecisionManufacturing sx={{ fontSize: 50, color: '#f59e0b' }} />,
    image: BRAND.images.facilityExterior,
    price: 'KES 1,000-50,000 per piece',
  },
  {
    id: '4',
    title: 'Sustainable Packaging Solutions',
    description: 'Eco-friendly packaging materials and solutions',
    category: 'Packaging',
    features: [
      '100% biodegradable',
      'Recyclable materials',
      'Custom branding',
      'Cost-effective',
    ],
    specifications: [
      'Material: Recycled paper/cardboard',
      'Printing: Water-based inks',
      'Strength: 200-500 GSM',
      'Certification: FSC, ISO 14001',
    ],
    applications: [
      'Food packaging',
      'E-commerce',
      'Retail packaging',
      'Industrial packaging',
    ],
    icon: <Park sx={{ fontSize: 50, color: '#10b981' }} />,
    image: BRAND.images.teamFacility,
    price: 'KES 50-500 per unit',
  },
  {
    id: '5',
    title: 'Smart Home Appliances',
    description: 'Intelligent home appliances with modern technology',
    category: 'Appliances',
    features: [
      'Smart connectivity',
      'Energy efficient',
      'Voice control',
      'Mobile app control',
    ],
    specifications: [
      'Power: 220-240V AC',
      'Connectivity: WiFi, Bluetooth',
      'Control: App, voice, manual',
      'Warranty: 2-3 years',
    ],
    applications: [
      'Residential homes',
      'Smart apartments',
      'Hotels and hospitality',
      'Commercial spaces',
    ],
    icon: <AutoAwesome sx={{ fontSize: 50, color: '#8b5cf6' }} />,
    image: BRAND.images.gateSign,
    price: 'KES 15,000-200,000',
  },
  {
    id: '6',
    title: 'Industrial Safety Equipment',
    description: 'High-quality safety equipment for industrial applications',
    category: 'Safety',
    features: [
      'Safety certified',
      'Durable materials',
      'Comfortable fit',
      'High visibility',
    ],
    specifications: [
      'Standards: ANSI, EN, ISO',
      'Materials: High-visibility fabric',
      'Reflectivity: 360° coverage',
      'Durability: Industrial grade',
    ],
    applications: [
      'Construction sites',
      'Manufacturing facilities',
      'Mining operations',
      'Road construction',
    ],
    icon: <SupportAgent sx={{ fontSize: 50, color: '#ef4444' }} />,
    image: BRAND.images.outdoorSign,
    price: 'KES 2,000-25,000',
  },
];

const categories = [
  { name: 'All Products', value: 'all', icon: <Business /> },
  { name: 'Textiles', value: 'Textiles', icon: <ProductionQuantityLimits /> },
  { name: 'Furniture', value: 'Furniture', icon: <Build /> },
  { name: 'Metalworks', value: 'Metalworks', icon: <PrecisionManufacturing /> },
  { name: 'Packaging', value: 'Packaging', icon: <Park /> },
  { name: 'Appliances', value: 'Appliances', icon: <AutoAwesome /> },
  { name: 'Safety', value: 'Safety', icon: <SupportAgent /> },
];

const Products: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [selectedCategory, setSelectedCategory] = useState('all');
  const navigate = useNavigate();

  const filteredProducts = selectedCategory === 'all' 
    ? products 
    : products.filter(product => product.category === selectedCategory);

  return (
    <Box sx={{ bgcolor: BRAND.colors.creamLight, minHeight: '100vh' }}>
      <BrandHero
        title="Monio Cluster Products"
        subtitle={BRAND.furnitureTagline}
        description="Premium textiles, smart furniture, metalworks, and more — manufactured with the Monio Cluster standard of quality."
        backgroundImage={BRAND.images.furniture}
        primaryAction={{ label: 'Request a Quote', onClick: () => navigate('/quote') }}
      />

      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* Category Filter */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 4, textAlign: 'center' }}>
            Our Product Categories
          </Typography>
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={2} 
            flexWrap="wrap"
            justifyContent="center"
            sx={{ mb: 6 }}
          >
            {categories.map((category) => (
              <Chip
                key={category.value}
                label={category.name}
                onClick={() => setSelectedCategory(category.value)}
                icon={category.icon}
                sx={{
                  background: selectedCategory === category.value 
                    ? 'linear-gradient(135deg, #6366f1 0%, #06b6d4 100%)'
                    : 'rgba(255, 255, 255, 0.1)',
                  color: selectedCategory === category.value ? 'white' : 'text.primary',
                  border: selectedCategory === category.value 
                    ? 'none' 
                    : '1px solid rgba(99, 102, 241, 0.2)',
                  '&:hover': {
                    background: selectedCategory === category.value 
                      ? 'linear-gradient(135deg, #4f46e5 0%, #0891b2 100%)'
                      : 'rgba(99, 102, 241, 0.1)',
                  },
                }}
              />
            ))}
          </Stack>
        </Box>

        {/* Products Grid */}
        <Grid container spacing={4}>
          {filteredProducts.map((product) => (
            <Grid item xs={12} md={6} lg={4} key={product.id}>
              <Card
                sx={{
                  height: '100%',
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 3,
                  boxShadow: '0px 20px 25px rgba(0, 0, 0, 0.1), 0px 10px 10px rgba(0, 0, 0, 0.04)',
                  transition: 'all 0.3s ease',
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0px 25px 50px rgba(0, 0, 0, 0.15)',
                  },
                }}
              >
                <Box
                  sx={{
                    height: 200,
                    background: `linear-gradient(135deg, #6366f1 0%, #06b6d4 100%)`,
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                />
                
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Chip 
                      label={product.category} 
                      size="small" 
                      sx={{ 
                        background: 'rgba(99, 102, 241, 0.1)',
                        color: '#6366f1',
                        fontWeight: 500,
                      }} 
                    />
                    <Typography variant="h6" sx={{ ml: 'auto', color: '#10b981', fontWeight: 600 }}>
                      {product.price}
                    </Typography>
                  </Box>
                  
                  <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                    {product.title}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {product.description}
                  </Typography>

                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    Key Features:
                  </Typography>
                  <List dense>
                    {product.features.slice(0, 3).map((feature, index) => (
                      <ListItem key={index} sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 24 }}>
                          <CheckCircle sx={{ fontSize: 16, color: '#10b981' }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={feature} 
                          primaryTypographyProps={{ 
                            variant: 'body2',
                            sx: { fontSize: '0.875rem' }
                          }} 
                        />
                      </ListItem>
                    ))}
                  </List>

                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, mt: 2 }}>
                    Applications:
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 3 }}>
                    {product.applications.slice(0, 2).map((app) => (
                      <Chip 
                        key={app} 
                        label={app} 
                        size="small" 
                        variant="outlined"
                        sx={{ 
                          borderColor: 'rgba(99, 102, 241, 0.3)',
                          color: '#6366f1',
                          mb: 1,
                        }} 
                      />
                    ))}
                  </Stack>

                  <Stack direction="row" spacing={2}>
                    <Button
                      variant="outlined"
                      fullWidth
                      sx={{
                        border: '2px solid #6366f1',
                        color: '#6366f1',
                        '&:hover': {
                          border: '2px solid #4f46e5',
                          background: 'rgba(99, 102, 241, 0.04)',
                        },
                      }}
                      onClick={() => navigate(`/products/display/${product.id}`)}
                    >
                      View Details
                    </Button>
                    <Button
                      variant="contained"
                      fullWidth
                      sx={{
                        background: 'linear-gradient(135deg, #6366f1 0%, #06b6d4 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #4f46e5 0%, #0891b2 100%)',
                        },
                      }}
                      onClick={() => navigate(`/quote?productId=${product.id}`)}
                    >
                      Get Quote
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Call to Action */}
        <Box sx={{ 
          mt: 8, 
          p: 6, 
          background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)', 
          borderRadius: 3,
          textAlign: 'center',
        }}>
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 700 }}>
            Need Custom Products?
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 4, maxWidth: '600px', mx: 'auto' }}>
            We specialize in custom manufacturing solutions. Let us create products tailored to your specific requirements.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              sx={{
                px: 6,
                py: 2,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #6366f1 0%, #06b6d4 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #4f46e5 0%, #0891b2 100%)',
                },
              }}
            >
              Request Custom Quote
            </Button>
            <Button
              variant="outlined"
              size="large"
              sx={{
                px: 6,
                py: 2,
                borderRadius: 3,
                border: '2px solid #6366f1',
                color: '#6366f1',
                '&:hover': {
                  border: '2px solid #4f46e5',
                  background: 'rgba(99, 102, 241, 0.04)',
                },
              }}
            >
              Download Catalog
            </Button>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};

export default Products;
